import { useEffect, useState } from "react";
import { api, formatApiError } from "../context/AuthContext";
import { useLang } from "../context/LanguageContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import PhotoDropzone, { resolveUrl } from "../components/PhotoDropzone";
import AiPricingPanel from "../components/AiPricingPanel";
import BookingCalendar from "../components/BookingCalendar";
import ClientesTable from "../components/ClientesTable";
import ConversationsPanel from "../components/ConversationsPanel";

const emptyHouse = {
  // public
  name: "",
  location: "",
  description: "",
  cover_image: "",
  photos: [],
  bedrooms: 1,
  bathrooms: 1,
  max_guests: 2,
  nightly_price: 0,
  is_public: true,
  // private (owner / client)
  owner_name: "",
  has_maintenance: false,
  address: "",
  postal_code: "",
  citizen_card_number: "",
  tax_number: "",
  owner_email: "",
  owner_phone: "",
  // extended client info
  key_code: "",
  signup_date: "",
  external_link: "",
  owner_birth_date: "",
  owner_personal_address: "",
  owner_personal_postal_code: "",
};
const emptyBooking = {
  house_id: "",
  guest_name: "",
  check_in: "",
  check_out: "",
  nights: 1,
  guests_count: 1,
  amount: 0,
};
const emptyCleaning = { house_id: "", date: "", cost: 0, status: "completed" };
const emptyMaintenance = {
  house_id: "",
  title: "",
  description: "",
  intervention_date: "",
  cost: 0,
  status: "pending",
  photos_before: [],
  photos_after: [],
};
const emptyReview = { house_id: "", guest_name: "", rating: 5, comment: "", source: "airbnb", date: "" };
const emptyTestimonial = {
  name: "",
  location: "",
  role: "Proprietário",
  quote: "",
  avatar_url: "",
  house_id: "",
  is_public: true,
  order: 0,
};

const field =
  "w-full bg-transparent border-b border-[#0A1128]/20 focus:border-[#C5A059] outline-none py-2 text-[#0A1128] text-sm";

const AdminPage = () => {
  const { t } = useLang();
  const [houses, setHouses] = useState([]);
  const [users, setUsers] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [cleanings, setCleanings] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [simulations, setSimulations] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [maintenances, setMaintenances] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [testimonials, setTestimonials] = useState([]);

  const [newHouse, setNewHouse] = useState(emptyHouse);
  const [editingHouseId, setEditingHouseId] = useState(null);
  const [newBooking, setNewBooking] = useState(emptyBooking);
  const [newCleaning, setNewCleaning] = useState(emptyCleaning);
  const [newMaintenance, setNewMaintenance] = useState(emptyMaintenance);
  const [newReview, setNewReview] = useState(emptyReview);
  const [newTestimonial, setNewTestimonial] = useState(emptyTestimonial);

  const loadAll = async () => {
    const [h, u, b, c, ct, m, s, r, mt, rv, ts] = await Promise.all([
      api.get("/houses"),
      api.get("/admin/users"),
      api.get("/bookings"),
      api.get("/cleanings"),
      api.get("/contact"),
      api.get("/meetings"),
      api.get("/simulations"),
      api.get("/reservations"),
      api.get("/maintenances"),
      api.get("/reviews"),
      api.get("/testimonials"),
    ]);
    setHouses(h.data);
    setUsers(u.data);
    setBookings(b.data);
    setCleanings(c.data);
    setContacts(ct.data);
    setMeetings(m.data);
    setSimulations(s.data);
    setReservations(r.data);
    setMaintenances(mt.data);
    setReviews(rv.data);
    setTestimonials(ts.data);
  };

  const approveReservation = async (id) => {
    await api.post(`/reservations/${id}/approve`);
    toast.success("OK");
    loadAll();
  };
  const rejectReservation = async (id) => {
    await api.post(`/reservations/${id}/reject`);
    loadAll();
  };

  useEffect(() => {
    loadAll().catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
    // Premium welcome toast for the admin with today's headline numbers.
    // Only the first time per browser session — avoids spamming on every refresh.
    if (typeof window !== "undefined" && !sessionStorage.getItem("stayzen-admin-welcomed")) {
      api.get("/admin/today").then(({ data }) => {
        const parts = [];
        if (data.new_bookings_today) parts.push(`${data.new_bookings_today} reserva${data.new_bookings_today > 1 ? "s" : ""} nova${data.new_bookings_today > 1 ? "s" : ""}`);
        if (data.pending_reservations) parts.push(`${data.pending_reservations} pedido${data.pending_reservations > 1 ? "s" : ""} pendente${data.pending_reservations > 1 ? "s" : ""}`);
        if (data.check_ins_today) parts.push(`${data.check_ins_today} check-in${data.check_ins_today > 1 ? "s" : ""} hoje`);
        if (data.check_outs_today) parts.push(`${data.check_outs_today} check-out${data.check_outs_today > 1 ? "s" : ""} hoje`);
        if (data.pending_maintenance) parts.push(`${data.pending_maintenance} manutenç${data.pending_maintenance > 1 ? "ões" : "ão"} aberta${data.pending_maintenance > 1 ? "s" : ""}`);

        const description = parts.length > 0
          ? parts.join(" · ")
          : "Tudo tranquilo hoje. Sem novidades em sua casa.";

        toast(`Olá, Admin`, {
          description,
          duration: 6000,
          className: "bg-[#0A1128] text-[#F4F1EB] border-[#C5A059]/40",
        });
        sessionStorage.setItem("stayzen-admin-welcomed", "1");
      }).catch(() => {});
    }
  }, []);

  const submitHouse = async (e) => {
    e.preventDefault();
    try {
      if (editingHouseId) {
        await api.put(`/houses/${editingHouseId}`, newHouse);
      } else {
        await api.post("/houses", newHouse);
      }
      setNewHouse(emptyHouse);
      setEditingHouseId(null);
      toast.success("OK");
      await loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const startEditHouse = (h) => {
    setEditingHouseId(h.id);
    setNewHouse({ ...emptyHouse, ...h, photos: h.photos || [] });
    window.scrollTo({ top: 400, behavior: "smooth" });
  };
  const cancelEditHouse = () => {
    setEditingHouseId(null);
    setNewHouse(emptyHouse);
  };
  const addPhoto = () => {
    if (newHouse.photos.length >= 10) return;
    setNewHouse({ ...newHouse, photos: [...newHouse.photos, ""] });
  };
  const updatePhoto = (i, v) => {
    const copy = [...newHouse.photos];
    copy[i] = v;
    setNewHouse({ ...newHouse, photos: copy });
  };
  const removePhoto = (i) => {
    setNewHouse({ ...newHouse, photos: newHouse.photos.filter((_, idx) => idx !== i) });
  };
  const deleteHouse = async (id) => {
    await api.delete(`/houses/${id}`);
    loadAll();
  };
  const assignHouse = async (userId, houseId) => {
    await api.post("/admin/assign-house", { user_id: userId, house_id: houseId || null });
    toast.success("OK");
    loadAll();
  };
  const addHouseToUser = async (userId, houseId) => {
    if (!houseId) return;
    await api.post("/admin/assign-house", { user_id: userId, add_house_id: houseId });
    toast.success("Casa adicionada");
    loadAll();
  };
  const removeHouseFromUser = async (userId, houseId) => {
    await api.post("/admin/assign-house", { user_id: userId, remove_house_id: houseId });
    toast.success("Casa removida");
    loadAll();
  };
  const submitBooking = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...newBooking,
        nights: Number(newBooking.nights),
        guests_count: Number(newBooking.guests_count),
        amount: Number(newBooking.amount),
      };
      await api.post("/bookings", payload);
      setNewBooking(emptyBooking);
      toast.success("OK");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const deleteBooking = async (id) => {
    await api.delete(`/bookings/${id}`);
    loadAll();
  };
  const submitCleaning = async (e) => {
    e.preventDefault();
    try {
      await api.post("/cleanings", { ...newCleaning, cost: Number(newCleaning.cost) });
      setNewCleaning(emptyCleaning);
      toast.success("OK");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const deleteCleaning = async (id) => {
    await api.delete(`/cleanings/${id}`);
    loadAll();
  };
  const submitMaintenance = async (e) => {
    e.preventDefault();
    try {
      await api.post("/maintenances", { ...newMaintenance, cost: Number(newMaintenance.cost) });
      setNewMaintenance(emptyMaintenance);
      toast.success("OK");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const deleteMaintenance = async (id) => {
    await api.delete(`/maintenances/${id}`);
    loadAll();
  };
  const submitReview = async (e) => {
    e.preventDefault();
    try {
      await api.post("/reviews", { ...newReview, rating: Number(newReview.rating) });
      setNewReview(emptyReview);
      toast.success("OK");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const deleteReview = async (id) => {
    await api.delete(`/reviews/${id}`);
    loadAll();
  };
  const submitTestimonial = async (e) => {
    e.preventDefault();
    try {
      await api.post("/testimonials", { ...newTestimonial, order: Number(newTestimonial.order) });
      setNewTestimonial(emptyTestimonial);
      toast.success("OK");
      loadAll();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };
  const deleteTestimonial = async (id) => {
    await api.delete(`/testimonials/${id}`);
    loadAll();
  };

  return (
    <div className="bg-[#F4F1EB] min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-7xl w-full mx-auto px-6 md:px-12 py-16" data-testid="admin-panel">
        <div className="overline mb-3">Admin</div>
        <h1 className="font-serif-display text-4xl md:text-5xl font-light text-[#0A1128]">
          {t.admin.title}
        </h1>
        <p className="mt-3 text-[#3A435E] font-light">{t.admin.subtitle}</p>

        <Tabs defaultValue="houses" className="mt-10">
          <TabsList
            className="bg-[#FCFAF8] border border-[#0A1128]/10 p-0 h-auto gap-0 rounded-none"
            data-testid="admin-tabs"
          >
            {["houses", "calendar", "users", "bookings", "cleanings", "maintenance", "reviews", "testimonials", "reservations", "meetings", "conversations", "simulations", "ai_pricing"].map((k) => (
              <TabsTrigger
                key={k}
                value={k}
                className="data-[state=active]:bg-[#0A1128] data-[state=active]:text-[#F4F1EB] rounded-none px-6 py-3 text-xs tracking-[0.2em] uppercase"
                data-testid={`tab-${k}`}
              >
                {t.admin.tabs[k]}
              </TabsTrigger>
            ))}
          </TabsList>

          {/* HOUSES */}
          <TabsContent value="houses" className="mt-8 space-y-10">
            <form
              onSubmit={submitHouse}
              className="bg-[#FCFAF8] border border-[#0A1128]/10 p-6 md:p-8 space-y-10"
              data-testid="add-house-form"
            >
              <div className="flex items-center justify-between">
                <h3 className="font-serif-display text-2xl md:text-3xl font-light text-[#0A1128]">
                  {editingHouseId ? "Editar casa" : t.admin.add_house}
                </h3>
                {editingHouseId && (
                  <button
                    type="button"
                    onClick={cancelEditHouse}
                    className="text-xs uppercase tracking-[0.2em] text-[#3A435E] hover:text-[#0A1128]"
                    data-testid="cancel-edit-house"
                  >
                    {t.admin.cancel}
                  </button>
                )}
              </div>

              {/* SECTION 1 — Public */}
              <div>
                <div className="overline mb-4">Dados públicos (visíveis no site)</div>
                <div className="grid md:grid-cols-3 gap-4">
                  <input required placeholder="Nome" value={newHouse.name} onChange={(e) => setNewHouse({ ...newHouse, name: e.target.value })} className={field} data-testid="house-name" />
                  <input required placeholder="Localização (ex: Lisboa, Portugal)" value={newHouse.location} onChange={(e) => setNewHouse({ ...newHouse, location: e.target.value })} className={field} data-testid="house-location" />
                  <input type="number" min={0} placeholder="Preço / noite (€)" value={newHouse.nightly_price} onChange={(e) => setNewHouse({ ...newHouse, nightly_price: Number(e.target.value) })} className={field} data-testid="house-price" />
                  <input type="number" min={0} placeholder="Quartos" value={newHouse.bedrooms} onChange={(e) => setNewHouse({ ...newHouse, bedrooms: Number(e.target.value) })} className={field} data-testid="house-bedrooms" />
                  <input type="number" min={0} placeholder="Casas de banho" value={newHouse.bathrooms} onChange={(e) => setNewHouse({ ...newHouse, bathrooms: Number(e.target.value) })} className={field} data-testid="house-bathrooms" />
                  <input type="number" min={1} placeholder="Hóspedes máx." value={newHouse.max_guests} onChange={(e) => setNewHouse({ ...newHouse, max_guests: Number(e.target.value) })} className={field} data-testid="house-max-guests" />
                  <textarea placeholder="Descrição" value={newHouse.description} onChange={(e) => setNewHouse({ ...newHouse, description: e.target.value })} className={`${field} md:col-span-3`} rows={2} data-testid="house-description" />
                </div>
              </div>

              {/* SECTION 2 — Photos */}
              <div>
                <div className="flex items-center justify-between mb-4">
                  <div className="overline">Fotografias (até 10)</div>
                  <span className="text-xs text-[#3A435E]">{newHouse.photos.length}/10</span>
                </div>
                <PhotoDropzone
                  photos={newHouse.photos}
                  onChange={(next) => setNewHouse({
                    ...newHouse,
                    photos: next,
                    // auto-set cover to first photo if none
                    cover_image: newHouse.cover_image || next[0] || "",
                  })}
                  cover={newHouse.cover_image}
                  onCoverChange={(url) => setNewHouse({ ...newHouse, cover_image: url })}
                  max={10}
                />
              </div>

              {/* SECTION 3 — Private owner info */}
              <div>
                <div className="overline mb-4 text-[#9B2226]/80">Dados do cliente / dono (privados)</div>

                {/* Existing client picker — admin-only shortcut for owners with multiple houses */}
                {(() => {
                  const uniqueClients = [];
                  const seen = new Set();
                  houses.forEach((h) => {
                    if (!h.owner_name) return;
                    const key = (h.tax_number || h.owner_email || h.owner_name || "").toLowerCase().trim();
                    if (!key || seen.has(key)) return;
                    seen.add(key);
                    uniqueClients.push({ key, house: h });
                  });
                  if (uniqueClients.length === 0) return null;
                  return (
                    <div className="mb-6 bg-[#0A1128]/[0.04] border border-dashed border-[#0A1128]/25 p-4">
                      <div className="text-[10px] tracking-[0.2em] uppercase text-[#3A435E] mb-2">
                        Cliente existente (só admin) — copiar dados pessoais
                      </div>
                      <select
                        value=""
                        onChange={(e) => {
                          const picked = uniqueClients.find((c) => c.key === e.target.value);
                          if (!picked) return;
                          const h = picked.house;
                          setNewHouse((prev) => ({
                            ...prev,
                            owner_name: h.owner_name || "",
                            owner_email: h.owner_email || "",
                            owner_phone: h.owner_phone || "",
                            citizen_card_number: h.citizen_card_number || "",
                            tax_number: h.tax_number || "",
                            owner_birth_date: h.owner_birth_date || "",
                            owner_personal_address: h.owner_personal_address || "",
                            owner_personal_postal_code: h.owner_personal_postal_code || "",
                          }));
                          toast.success(`Dados de ${h.owner_name} copiados`);
                        }}
                        className={field}
                        data-testid="pick-existing-client"
                      >
                        <option value="">— Selecionar cliente existente —</option>
                        {uniqueClients.map(({ key, house: h }) => {
                          const count = houses.filter(
                            (x) => (x.tax_number || x.owner_email || x.owner_name || "").toLowerCase().trim() === key
                          ).length;
                          return (
                            <option key={key} value={key}>
                              {h.owner_name}{h.tax_number ? ` · NIF ${h.tax_number}` : ""}{count > 1 ? ` · ${count} casas` : ""}
                            </option>
                          );
                        })}
                      </select>
                      <div className="text-[10px] text-[#3A435E]/70 mt-2">
                        Ideal quando o mesmo proprietário tem mais que uma casa. Nenhum cliente vê esta lista.
                      </div>
                    </div>
                  );
                })()}

                {/* House & contract */}
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#3A435E] mb-2">Casa & contrato</div>
                <div className="grid md:grid-cols-3 gap-4">
                  <input placeholder="Código da chave" value={newHouse.key_code} onChange={(e) => setNewHouse({ ...newHouse, key_code: e.target.value })} className={field} data-testid="house-key-code" />
                  <label className="flex flex-col">
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#3A435E] mb-1">Data de inscrição</span>
                    <input type="date" value={newHouse.signup_date} onChange={(e) => setNewHouse({ ...newHouse, signup_date: e.target.value })} className={field} data-testid="house-signup-date" />
                  </label>
                  <input placeholder="Link da casa (Airbnb / Booking…)" value={newHouse.external_link} onChange={(e) => setNewHouse({ ...newHouse, external_link: e.target.value })} className={field} data-testid="house-external-link" />
                  <input placeholder="Morada da casa" value={newHouse.address} onChange={(e) => setNewHouse({ ...newHouse, address: e.target.value })} className={`${field} md:col-span-2`} data-testid="owner-address" />
                  <input placeholder="Código-postal da casa" value={newHouse.postal_code} onChange={(e) => setNewHouse({ ...newHouse, postal_code: e.target.value })} className={field} data-testid="owner-postal" />
                  <label className="flex items-center gap-3 text-sm text-[#0A1128] self-end pb-2 md:col-span-3" data-testid="owner-has-maintenance-label">
                    <input
                      type="checkbox"
                      checked={newHouse.has_maintenance}
                      onChange={(e) => setNewHouse({ ...newHouse, has_maintenance: e.target.checked })}
                      className="accent-[#C5A059] w-4 h-4"
                      data-testid="owner-has-maintenance"
                    />
                    Contrato de manutenção
                  </label>
                </div>

                {/* Personal */}
                <div className="text-[10px] tracking-[0.2em] uppercase text-[#3A435E] mb-2 mt-6">Dados pessoais do cliente</div>
                <div className="grid md:grid-cols-3 gap-4">
                  <input placeholder="Nome do cliente" value={newHouse.owner_name} onChange={(e) => setNewHouse({ ...newHouse, owner_name: e.target.value })} className={field} data-testid="owner-name" />
                  <input placeholder="Email / Gmail" type="email" value={newHouse.owner_email} onChange={(e) => setNewHouse({ ...newHouse, owner_email: e.target.value })} className={field} data-testid="owner-email" />
                  <input placeholder="Telefone / contacto" value={newHouse.owner_phone} onChange={(e) => setNewHouse({ ...newHouse, owner_phone: e.target.value })} className={field} data-testid="owner-phone" />
                  <input placeholder="Nº cartão de cidadão / BI" value={newHouse.citizen_card_number} onChange={(e) => setNewHouse({ ...newHouse, citizen_card_number: e.target.value })} className={field} data-testid="owner-cc" />
                  <input placeholder="Nº contribuinte (NIF)" value={newHouse.tax_number} onChange={(e) => setNewHouse({ ...newHouse, tax_number: e.target.value })} className={field} data-testid="owner-nif" />
                  <label className="flex flex-col">
                    <span className="text-[10px] tracking-[0.2em] uppercase text-[#3A435E] mb-1">Data de nascimento</span>
                    <input type="date" value={newHouse.owner_birth_date} onChange={(e) => setNewHouse({ ...newHouse, owner_birth_date: e.target.value })} className={field} data-testid="owner-birth-date" />
                  </label>
                  <input placeholder="Morada pessoal" value={newHouse.owner_personal_address} onChange={(e) => setNewHouse({ ...newHouse, owner_personal_address: e.target.value })} className={`${field} md:col-span-2`} data-testid="owner-personal-address" />
                  <input placeholder="Código-postal pessoal" value={newHouse.owner_personal_postal_code} onChange={(e) => setNewHouse({ ...newHouse, owner_personal_postal_code: e.target.value })} className={field} data-testid="owner-personal-postal" />
                </div>
              </div>

              <button
                type="submit"
                className="bg-[#0A1128] text-white hover:bg-[#162243] px-8 py-4 text-xs tracking-[0.22em] uppercase flex items-center gap-2"
                data-testid="submit-add-house"
              >
                <Plus size={14} /> {editingHouseId ? "Guardar alterações" : t.admin.add_house}
              </button>
            </form>

            <div className="grid md:grid-cols-2 gap-4">
              {houses.map((h) => (
                <div key={h.id} className="bg-[#FCFAF8] border border-[#0A1128]/10 p-5 flex gap-5" data-testid={`admin-house-${h.id}`}>
                  {h.cover_image && <img src={resolveUrl(h.cover_image)} alt={h.name} className="w-28 h-28 object-cover" />}
                  <div className="flex-1 min-w-0">
                    <div className="overline text-[#C5A059]">{h.location}</div>
                    <div className="font-serif-display text-xl text-[#0A1128]">{h.name}</div>
                    <div className="text-xs text-[#3A435E] mt-1">
                      {h.bedrooms} quartos · {h.bathrooms} wc · {h.max_guests} hóspedes {h.nightly_price ? `· €${Math.round(h.nightly_price)}/noite` : ""}
                    </div>
                    {h.owner_name && (
                      <div className="text-xs text-[#3A435E] mt-1 truncate">
                        Dono: <span className="text-[#0A1128]">{h.owner_name}</span>
                        {h.has_maintenance ? " · manutenção ✓" : ""}
                      </div>
                    )}
                    <div className="text-xs text-[#7B86A4] mt-1">{(h.photos || []).length} fotografias</div>
                  </div>
                  <div className="flex flex-col gap-3 items-end">
                    <button onClick={() => startEditHouse(h)} className="text-xs uppercase tracking-[0.18em] text-[#0A1128] hover:text-[#C5A059]" data-testid={`edit-house-${h.id}`}>
                      Editar
                    </button>
                    <button onClick={() => deleteHouse(h.id)} className="text-[#9B2226]" data-testid={`delete-house-${h.id}`}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>

          {/* USERS / CLIENTES */}
          <TabsContent value="users" className="mt-8 space-y-6">
            <ClientesTable houses={houses} onEditHouse={startEditHouse} />

            {/* Linked logins (compact, kept for assigning house to login accounts) */}
            <details className="bg-[#FCFAF8] border border-[#0A1128]/10">
              <summary className="px-5 py-3 text-xs tracking-[0.2em] uppercase text-[#3A435E] cursor-pointer hover:text-[#0A1128]" data-testid="toggle-user-accounts">
                Contas de login ({users.length}) — atribuir casas
              </summary>
              <div className="overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-[#F4F1EB] text-[#3A435E]">
                    <tr>
                      <th className="text-left px-5 py-3 font-normal">Nome</th>
                      <th className="text-left px-5 py-3 font-normal">Email</th>
                      <th className="text-left px-5 py-3 font-normal">Role</th>
                      <th className="text-left px-5 py-3 font-normal">Casas atribuídas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => {
                      const assignedIds = (u.house_ids && u.house_ids.length > 0)
                        ? u.house_ids
                        : (u.house_id ? [u.house_id] : []);
                      const availableHouses = houses.filter((h) => !assignedIds.includes(h.id));
                      return (
                        <tr key={u.id} className="border-t border-[#0A1128]/10 align-top" data-testid={`user-row-${u.id}`}>
                          <td className="px-5 py-3 whitespace-nowrap">{u.name}</td>
                          <td className="px-5 py-3 whitespace-nowrap">{u.email}</td>
                          <td className="px-5 py-3">{u.role}</td>
                          <td className="px-5 py-3">
                            {u.role === "admin" ? (
                              "—"
                            ) : (
                              <div className="flex flex-col gap-2" data-testid={`assign-cell-${u.id}`}>
                                {assignedIds.length === 0 ? (
                                  <span className="text-xs text-[#3A435E]/70">Sem casas</span>
                                ) : (
                                  <div className="flex flex-wrap gap-2">
                                    {assignedIds.map((hid) => {
                                      const h = houses.find((x) => x.id === hid);
                                      const label = h?.name || hid;
                                      return (
                                        <span
                                          key={hid}
                                          className="inline-flex items-center gap-2 bg-[#F4F1EB] border border-[#0A1128]/15 px-2.5 py-1 text-xs text-[#0A1128]"
                                          data-testid={`assigned-house-${u.id}-${hid}`}
                                        >
                                          {label}
                                          <button
                                            type="button"
                                            onClick={() => removeHouseFromUser(u.id, hid)}
                                            className="text-[#3A435E] hover:text-[#0A1128] text-sm leading-none"
                                            title="Remover casa"
                                            data-testid={`remove-house-${u.id}-${hid}`}
                                          >
                                            ×
                                          </button>
                                        </span>
                                      );
                                    })}
                                  </div>
                                )}
                                <select
                                  value=""
                                  disabled={availableHouses.length === 0}
                                  onChange={(e) => addHouseToUser(u.id, e.target.value)}
                                  className="bg-transparent border border-[#0A1128]/15 px-2 py-1 text-xs w-full max-w-[240px] disabled:opacity-40"
                                  data-testid={`add-house-select-${u.id}`}
                                >
                                  <option value="">
                                    {availableHouses.length === 0 ? "— Todas as casas atribuídas —" : "+ Adicionar casa…"}
                                  </option>
                                  {availableHouses.map((h) => (
                                    <option key={h.id} value={h.id}>{h.name}</option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </details>
          </TabsContent>

          {/* BOOKINGS */}
          <TabsContent value="bookings" className="mt-8 space-y-10">
            <form onSubmit={submitBooking} className="bg-[#FCFAF8] border border-[#0A1128]/10 p-6 grid md:grid-cols-3 gap-4" data-testid="add-booking-form">
              <select required value={newBooking.house_id} onChange={(e) => setNewBooking({ ...newBooking, house_id: e.target.value })} className={field}>
                <option value="">{t.admin.fields.house_id}</option>
                {houses.map((h) => (<option key={h.id} value={h.id}>{h.name}</option>))}
              </select>
              <input required placeholder={t.admin.fields.guest_name} value={newBooking.guest_name} onChange={(e) => setNewBooking({ ...newBooking, guest_name: e.target.value })} className={field} />
              <input required type="date" value={newBooking.check_in} onChange={(e) => setNewBooking({ ...newBooking, check_in: e.target.value })} className={field} />
              <input required type="date" value={newBooking.check_out} onChange={(e) => setNewBooking({ ...newBooking, check_out: e.target.value })} className={field} />
              <input required type="number" min={1} placeholder={t.admin.fields.nights} value={newBooking.nights} onChange={(e) => setNewBooking({ ...newBooking, nights: e.target.value })} className={field} />
              <input required type="number" min={1} placeholder={t.admin.fields.guests_count} value={newBooking.guests_count} onChange={(e) => setNewBooking({ ...newBooking, guests_count: e.target.value })} className={field} />
              <input required type="number" min={0} placeholder={t.admin.fields.amount} value={newBooking.amount} onChange={(e) => setNewBooking({ ...newBooking, amount: e.target.value })} className={field} />
              <button type="submit" className="md:col-span-3 bg-[#0A1128] text-white hover:bg-[#162243] px-6 py-3 text-xs tracking-[0.22em] uppercase flex items-center justify-center gap-2" data-testid="submit-add-booking">
                <Plus size={14} /> {t.admin.add_booking}
              </button>
            </form>

            <div className="bg-[#FCFAF8] border border-[#0A1128]/10 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F1EB] text-[#3A435E]">
                  <tr>
                    <th className="text-left px-5 py-3 font-normal">{t.dashboard.table.guest}</th>
                    <th className="text-left px-5 py-3 font-normal">{t.dashboard.table.check_in}</th>
                    <th className="text-left px-5 py-3 font-normal">{t.dashboard.table.check_out}</th>
                    <th className="text-left px-5 py-3 font-normal">{t.dashboard.table.nights}</th>
                    <th className="text-left px-5 py-3 font-normal">{t.dashboard.table.guests}</th>
                    <th className="text-left px-5 py-3 font-normal">{t.dashboard.table.amount}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-t border-[#0A1128]/10" data-testid={`admin-booking-${b.id}`}>
                      <td className="px-5 py-3">{b.guest_name}</td>
                      <td className="px-5 py-3">{b.check_in}</td>
                      <td className="px-5 py-3">{b.check_out}</td>
                      <td className="px-5 py-3">{b.nights}</td>
                      <td className="px-5 py-3">{b.guests_count}</td>
                      <td className="px-5 py-3">€{b.amount}</td>
                      <td className="px-5 py-3 text-right">
                        <button onClick={() => deleteBooking(b.id)} className="text-[#9B2226]" data-testid={`delete-booking-${b.id}`}><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* CLEANINGS */}
          <TabsContent value="cleanings" className="mt-8 space-y-10">
            <form onSubmit={submitCleaning} className="bg-[#FCFAF8] border border-[#0A1128]/10 p-6 grid md:grid-cols-3 gap-4" data-testid="add-cleaning-form">
              <select required value={newCleaning.house_id} onChange={(e) => setNewCleaning({ ...newCleaning, house_id: e.target.value })} className={field}>
                <option value="">{t.admin.fields.house_id}</option>
                {houses.map((h) => (<option key={h.id} value={h.id}>{h.name}</option>))}
              </select>
              <input required type="date" value={newCleaning.date} onChange={(e) => setNewCleaning({ ...newCleaning, date: e.target.value })} className={field} />
              <input type="number" min={0} placeholder={t.admin.fields.cost} value={newCleaning.cost} onChange={(e) => setNewCleaning({ ...newCleaning, cost: e.target.value })} className={field} />
              <button type="submit" className="md:col-span-3 bg-[#0A1128] text-white hover:bg-[#162243] px-6 py-3 text-xs tracking-[0.22em] uppercase flex items-center justify-center gap-2" data-testid="submit-add-cleaning">
                <Plus size={14} /> {t.admin.add_cleaning}
              </button>
            </form>

            <div className="bg-[#FCFAF8] border border-[#0A1128]/10 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F1EB] text-[#3A435E]">
                  <tr>
                    <th className="text-left px-5 py-3 font-normal">{t.admin.fields.date}</th>
                    <th className="text-left px-5 py-3 font-normal">{t.admin.fields.house_id}</th>
                    <th className="text-left px-5 py-3 font-normal">{t.admin.fields.cost}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {cleanings.map((c) => {
                    const house = houses.find((h) => h.id === c.house_id);
                    return (
                      <tr key={c.id} className="border-t border-[#0A1128]/10" data-testid={`admin-cleaning-${c.id}`}>
                        <td className="px-5 py-3">{c.date}</td>
                        <td className="px-5 py-3">{house?.name || "—"}</td>
                        <td className="px-5 py-3">€{c.cost}</td>
                        <td className="px-5 py-3 text-right">
                          <button onClick={() => deleteCleaning(c.id)} className="text-[#9B2226]" data-testid={`delete-cleaning-${c.id}`}><Trash2 size={14} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* MAINTENANCE */}
          <TabsContent value="maintenance" className="mt-8 space-y-10">
            <form onSubmit={submitMaintenance} className="bg-[#FCFAF8] border border-[#0A1128]/10 p-6 grid md:grid-cols-3 gap-4" data-testid="add-maintenance-form">
              <select required value={newMaintenance.house_id} onChange={(e) => setNewMaintenance({ ...newMaintenance, house_id: e.target.value })} className={field}>
                <option value="">Casa</option>
                {houses.filter((h) => h.has_maintenance).map((h) => (<option key={h.id} value={h.id}>{h.name}</option>))}
              </select>
              <input required placeholder="Título (ex: Substituição torneira)" value={newMaintenance.title} onChange={(e) => setNewMaintenance({ ...newMaintenance, title: e.target.value })} className={field} />
              <input required type="date" value={newMaintenance.intervention_date} onChange={(e) => setNewMaintenance({ ...newMaintenance, intervention_date: e.target.value })} className={field} />
              <input type="number" min={0} placeholder="Custo (€)" value={newMaintenance.cost} onChange={(e) => setNewMaintenance({ ...newMaintenance, cost: e.target.value })} className={field} />
              <select value={newMaintenance.status} onChange={(e) => setNewMaintenance({ ...newMaintenance, status: e.target.value })} className={field}>
                <option value="pending">Pendente</option>
                <option value="in_progress">Em curso</option>
                <option value="completed">Concluído</option>
              </select>
              <input placeholder="URL fotos antes (separadas por vírgula)" value={newMaintenance.photos_before.join(",")} onChange={(e) => setNewMaintenance({ ...newMaintenance, photos_before: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className={field} />
              <textarea placeholder="Descrição do problema" value={newMaintenance.description} onChange={(e) => setNewMaintenance({ ...newMaintenance, description: e.target.value })} className={`${field} md:col-span-2`} rows={2} />
              <input placeholder="URL fotos depois (separadas por vírgula)" value={newMaintenance.photos_after.join(",")} onChange={(e) => setNewMaintenance({ ...newMaintenance, photos_after: e.target.value.split(",").map(s => s.trim()).filter(Boolean) })} className={field} />
              <button type="submit" className="md:col-span-3 bg-[#0A1128] text-white hover:bg-[#162243] px-6 py-3 text-xs tracking-[0.22em] uppercase flex items-center justify-center gap-2" data-testid="submit-add-maintenance">
                <Plus size={14} /> Adicionar manutenção
              </button>
            </form>

            <div className="bg-[#FCFAF8] border border-[#0A1128]/10 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F1EB] text-[#3A435E]">
                  <tr>
                    <th className="text-left px-5 py-3 font-normal">Data</th>
                    <th className="text-left px-5 py-3 font-normal">Casa</th>
                    <th className="text-left px-5 py-3 font-normal">Título</th>
                    <th className="text-left px-5 py-3 font-normal">Custo</th>
                    <th className="text-left px-5 py-3 font-normal">Estado</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {maintenances.length === 0 && (<tr><td colSpan={6} className="px-5 py-6 text-[#3A435E]">Sem manutenções registadas.</td></tr>)}
                  {maintenances.map((m) => {
                    const house = houses.find((h) => h.id === m.house_id);
                    const labelMap = { pending: "Pendente", in_progress: "Em curso", completed: "Concluído" };
                    const colorMap = { pending: "text-[#C5A059]", in_progress: "text-[#3A435E]", completed: "text-[#2D6A4F]" };
                    return (
                      <tr key={m.id} className="border-t border-[#0A1128]/10" data-testid={`admin-maintenance-${m.id}`}>
                        <td className="px-5 py-3">{m.intervention_date}</td>
                        <td className="px-5 py-3">{house?.name || "—"}</td>
                        <td className="px-5 py-3">{m.title}</td>
                        <td className="px-5 py-3">€{m.cost}</td>
                        <td className={`px-5 py-3 ${colorMap[m.status] || ""}`}>{labelMap[m.status] || m.status}</td>
                        <td className="px-5 py-3 text-right">
                          <button onClick={() => deleteMaintenance(m.id)} className="text-[#9B2226]" data-testid={`delete-maintenance-${m.id}`}><Trash2 size={14} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* REVIEWS */}
          <TabsContent value="reviews" className="mt-8 space-y-10">
            <form onSubmit={submitReview} className="bg-[#FCFAF8] border border-[#0A1128]/10 p-6 grid md:grid-cols-3 gap-4" data-testid="add-review-form">
              <select required value={newReview.house_id} onChange={(e) => setNewReview({ ...newReview, house_id: e.target.value })} className={field}>
                <option value="">Casa</option>
                {houses.map((h) => (<option key={h.id} value={h.id}>{h.name}</option>))}
              </select>
              <input required placeholder="Nome do hóspede" value={newReview.guest_name} onChange={(e) => setNewReview({ ...newReview, guest_name: e.target.value })} className={field} />
              <input required type="date" value={newReview.date} onChange={(e) => setNewReview({ ...newReview, date: e.target.value })} className={field} />
              <input required type="number" min={1} max={5} step="0.1" placeholder="Avaliação (1-5)" value={newReview.rating} onChange={(e) => setNewReview({ ...newReview, rating: e.target.value })} className={field} />
              <select value={newReview.source} onChange={(e) => setNewReview({ ...newReview, source: e.target.value })} className={field}>
                <option value="airbnb">Airbnb</option>
                <option value="booking">Booking</option>
                <option value="google">Google</option>
                <option value="internal">Direto</option>
              </select>
              <textarea placeholder="Comentário do hóspede" value={newReview.comment} onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })} className={`${field} md:col-span-3`} rows={2} />
              <button type="submit" className="md:col-span-3 bg-[#0A1128] text-white hover:bg-[#162243] px-6 py-3 text-xs tracking-[0.22em] uppercase flex items-center justify-center gap-2" data-testid="submit-add-review">
                <Plus size={14} /> Adicionar avaliação
              </button>
            </form>

            <div className="bg-[#FCFAF8] border border-[#0A1128]/10 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F1EB] text-[#3A435E]">
                  <tr>
                    <th className="text-left px-5 py-3 font-normal">Data</th>
                    <th className="text-left px-5 py-3 font-normal">Casa</th>
                    <th className="text-left px-5 py-3 font-normal">Hóspede</th>
                    <th className="text-left px-5 py-3 font-normal">Avaliação</th>
                    <th className="text-left px-5 py-3 font-normal">Plataforma</th>
                    <th className="text-left px-5 py-3 font-normal">Comentário</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {reviews.length === 0 && (<tr><td colSpan={7} className="px-5 py-6 text-[#3A435E]">Sem avaliações registadas.</td></tr>)}
                  {reviews.map((r) => {
                    const house = houses.find((h) => h.id === r.house_id);
                    const fullStars = Math.round(Number(r.rating || 0));
                    return (
                      <tr key={r.id} className="border-t border-[#0A1128]/10" data-testid={`admin-review-${r.id}`}>
                        <td className="px-5 py-3">{r.date}</td>
                        <td className="px-5 py-3">{house?.name || "—"}</td>
                        <td className="px-5 py-3">{r.guest_name}</td>
                        <td className="px-5 py-3 text-[#C5A059]">
                          {"★".repeat(fullStars)}{"☆".repeat(5 - fullStars)} <span className="text-[#3A435E] text-xs">{Number(r.rating).toFixed(1)}</span>
                        </td>
                        <td className="px-5 py-3 capitalize text-xs">{r.source}</td>
                        <td className="px-5 py-3 max-w-md truncate text-xs text-[#3A435E]" title={r.comment}>{r.comment}</td>
                        <td className="px-5 py-3 text-right">
                          <button onClick={() => deleteReview(r.id)} className="text-[#9B2226]" data-testid={`delete-review-${r.id}`}><Trash2 size={14} /></button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* TESTIMONIALS */}
          <TabsContent value="testimonials" className="mt-8 space-y-10">
            <form onSubmit={submitTestimonial} className="bg-[#FCFAF8] border border-[#0A1128]/10 p-6 grid md:grid-cols-3 gap-4" data-testid="add-testimonial-form">
              <input required placeholder="Nome do proprietário" value={newTestimonial.name} onChange={(e) => setNewTestimonial({ ...newTestimonial, name: e.target.value })} className={field} />
              <input placeholder="Localização (ex: Cascais, Portugal)" value={newTestimonial.location} onChange={(e) => setNewTestimonial({ ...newTestimonial, location: e.target.value })} className={field} />
              <input placeholder="Cargo / casa (ex: Proprietário · Villa)" value={newTestimonial.role} onChange={(e) => setNewTestimonial({ ...newTestimonial, role: e.target.value })} className={field} />
              <input placeholder="URL da foto (opcional)" value={newTestimonial.avatar_url} onChange={(e) => setNewTestimonial({ ...newTestimonial, avatar_url: e.target.value })} className={`${field} md:col-span-2`} />
              <input type="number" placeholder="Ordem" value={newTestimonial.order} onChange={(e) => setNewTestimonial({ ...newTestimonial, order: e.target.value })} className={field} />
              <textarea required placeholder="Testemunho (frase do proprietário)" value={newTestimonial.quote} onChange={(e) => setNewTestimonial({ ...newTestimonial, quote: e.target.value })} className={`${field} md:col-span-3`} rows={3} />
              <label className="flex items-center gap-3 text-sm text-[#0A1128]">
                <input type="checkbox" checked={newTestimonial.is_public} onChange={(e) => setNewTestimonial({ ...newTestimonial, is_public: e.target.checked })} className="accent-[#C5A059] w-4 h-4" />
                Mostrar no site público
              </label>
              <button type="submit" className="md:col-span-3 bg-[#0A1128] text-white hover:bg-[#162243] px-6 py-3 text-xs tracking-[0.22em] uppercase flex items-center justify-center gap-2" data-testid="submit-add-testimonial">
                <Plus size={14} /> Adicionar testemunho
              </button>
            </form>

            <div className="bg-[#FCFAF8] border border-[#0A1128]/10 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F1EB] text-[#3A435E]">
                  <tr>
                    <th className="text-left px-5 py-3 font-normal">Ordem</th>
                    <th className="text-left px-5 py-3 font-normal">Nome</th>
                    <th className="text-left px-5 py-3 font-normal">Cargo / Casa</th>
                    <th className="text-left px-5 py-3 font-normal">Citação</th>
                    <th className="text-center px-5 py-3 font-normal">Público</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {testimonials.length === 0 && (<tr><td colSpan={6} className="px-5 py-6 text-[#3A435E]">Sem testemunhos.</td></tr>)}
                  {testimonials.map((tst) => (
                    <tr key={tst.id} className="border-t border-[#0A1128]/10" data-testid={`admin-testimonial-${tst.id}`}>
                      <td className="px-5 py-3 text-xs">{tst.order}</td>
                      <td className="px-5 py-3">{tst.name}</td>
                      <td className="px-5 py-3 text-xs text-[#3A435E]">{tst.role || tst.location}</td>
                      <td className="px-5 py-3 max-w-md truncate text-xs text-[#3A435E]" title={tst.quote}>«{tst.quote}»</td>
                      <td className="px-5 py-3 text-center">{tst.is_public ? "✓" : "—"}</td>
                      <td className="px-5 py-3 text-right">
                        <button onClick={() => deleteTestimonial(tst.id)} className="text-[#9B2226]" data-testid={`delete-testimonial-${tst.id}`}><Trash2 size={14} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* RESERVATIONS (public requests) */}
          <TabsContent value="reservations" className="mt-8">
            <div className="bg-[#FCFAF8] border border-[#0A1128]/10 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F1EB] text-[#3A435E]">
                  <tr>
                    <th className="text-left px-5 py-3 font-normal">Casa</th>
                    <th className="text-left px-5 py-3 font-normal">Hóspede</th>
                    <th className="text-left px-5 py-3 font-normal">Contacto</th>
                    <th className="text-left px-5 py-3 font-normal">Check-in</th>
                    <th className="text-left px-5 py-3 font-normal">Check-out</th>
                    <th className="text-left px-5 py-3 font-normal">Noites</th>
                    <th className="text-left px-5 py-3 font-normal">Hóspedes</th>
                    <th className="text-left px-5 py-3 font-normal">Estado</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {reservations.length === 0 && (
                    <tr><td colSpan={9} className="px-5 py-6 text-[#3A435E]">{t.dashboard.empty}</td></tr>
                  )}
                  {reservations.map((r) => (
                    <tr key={r.id} className="border-t border-[#0A1128]/10" data-testid={`reservation-row-${r.id}`}>
                      <td className="px-5 py-3">{r.house_name || "—"}</td>
                      <td className="px-5 py-3">{r.name}</td>
                      <td className="px-5 py-3 text-xs text-[#3A435E]">{r.email}<br/>{r.phone || ""}</td>
                      <td className="px-5 py-3">{r.check_in}</td>
                      <td className="px-5 py-3">{r.check_out}</td>
                      <td className="px-5 py-3">{r.nights}</td>
                      <td className="px-5 py-3">{r.guests_count}</td>
                      <td className="px-5 py-3">
                        <span className={
                          r.status === "approved" ? "text-[#2D6A4F]" :
                          r.status === "rejected" ? "text-[#9B2226]" : "text-[#C5A059]"
                        }>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-right space-x-3">
                        {r.status === "pending" && (
                          <>
                            <button
                              onClick={() => approveReservation(r.id)}
                              className="text-xs uppercase tracking-widest text-[#2D6A4F] hover:underline"
                              data-testid={`approve-reservation-${r.id}`}
                            >
                              Aprovar
                            </button>
                            <button
                              onClick={() => rejectReservation(r.id)}
                              className="text-xs uppercase tracking-widest text-[#9B2226] hover:underline"
                              data-testid={`reject-reservation-${r.id}`}
                            >
                              Rejeitar
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* CONTACTS (removed from admin; still accessible if needed by direct API) */}

          {/* MEETINGS */}
          <TabsContent value="meetings" className="mt-8">
            <div className="bg-[#FCFAF8] border border-[#0A1128]/10 overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F4F1EB] text-[#3A435E]">
                  <tr>
                    <th className="text-left px-5 py-3 font-normal">Data</th>
                    <th className="text-left px-5 py-3 font-normal">Hora</th>
                    <th className="text-left px-5 py-3 font-normal">Nome</th>
                    <th className="text-left px-5 py-3 font-normal">Email</th>
                    <th className="text-left px-5 py-3 font-normal">Telefone</th>
                    <th className="text-left px-5 py-3 font-normal">Mensagem</th>
                  </tr>
                </thead>
                <tbody>
                  {meetings.length === 0 && (
                    <tr><td colSpan={6} className="px-5 py-6 text-[#3A435E]">{t.dashboard.empty}</td></tr>
                  )}
                  {meetings.map((m) => (
                    <tr key={m.id} className="border-t border-[#0A1128]/10" data-testid={`meeting-row-${m.id}`}>
                      <td className="px-5 py-3">{m.date}</td>
                      <td className="px-5 py-3">{m.time}</td>
                      <td className="px-5 py-3">{m.name}</td>
                      <td className="px-5 py-3">{m.email}</td>
                      <td className="px-5 py-3">{m.phone || "—"}</td>
                      <td className="px-5 py-3 max-w-xs truncate">{m.message || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          {/* CONVERSATIONS (AI Concierge leads) */}
          <TabsContent value="conversations" className="mt-8">
            <ConversationsPanel />
          </TabsContent>

          {/* SIMULATIONS */}
          <TabsContent value="simulations" className="mt-8">
            {/* Rich lead-qualifier table: shows the data captured by the calculator */}
            {(() => {
              const ZONE_LABELS = {
                principe_real: "Príncipe Real / Avenidas",
                baixa_chiado: "Baixa / Chiado",
                alfama: "Alfama / Castelo",
                belem: "Belém / Ajuda",
                parque_nacoes: "Parque das Nações",
                estrela_lapa: "Estrela / Lapa",
                campo_ourique: "Campo de Ourique",
                alvalade: "Alvalade / Areeiro",
                cascais: "Cascais / Estoril",
                sintra: "Sintra",
                almada: "Almada / Costa da Caparica",
                outra: "Outra zona",
                lisboa: "Lisboa (geral)",
                porto: "Porto",
                comporta: "Comporta / Alentejo",
                algarve: "Algarve",
                outro: "Outra",
              };
              const AMENITY_LABELS = {
                sea_view: "Vista mar/rio",
                pool: "Piscina",
                ac: "Ar condicionado",
                parking: "Estacionamento",
                balcony: "Varanda/terraço",
              };
              const AL_LABELS = {
                self: "Gestão própria",
                other_company: "Outra empresa gere",
                none: "Sem AL",
              };
              const AL_STYLE = {
                self: "bg-[#C5A059]/15 text-[#8B6A2C]",
                other_company: "bg-[#9B2226]/12 text-[#9B2226]",
                none: "bg-[#2D6A4F]/15 text-[#2D6A4F]",
              };
              return (
                <div className="bg-[#FCFAF8] border border-[#0A1128]/10 overflow-x-auto" data-testid="simulations-table">
                  <table className="w-full text-sm min-w-[1400px]">
                    <thead className="bg-[#F4F1EB] text-[#3A435E]">
                      <tr>
                        <th className="text-left px-4 py-3 font-normal text-xs">Data</th>
                        <th className="text-left px-4 py-3 font-normal text-xs">Contacto</th>
                        <th className="text-left px-4 py-3 font-normal text-xs">Tipologia</th>
                        <th className="text-left px-4 py-3 font-normal text-xs">Zona</th>
                        <th className="text-left px-4 py-3 font-normal text-xs">Comodidades</th>
                        <th className="text-left px-4 py-3 font-normal text-xs">Registo AL</th>
                        <th className="text-center px-4 py-3 font-normal text-xs">Noites</th>
                        <th className="text-right px-4 py-3 font-normal text-xs">Gestão própria</th>
                        <th className="text-right px-4 py-3 font-normal text-xs">Com StayZen</th>
                        <th className="text-right px-4 py-3 font-normal text-xs">Anual</th>
                        <th className="text-center px-4 py-3 font-normal text-xs">Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {simulations.length === 0 && (
                        <tr><td colSpan={11} className="px-5 py-8 text-center text-[#3A435E]">Sem simulações registadas.</td></tr>
                      )}
                      {simulations.map((s) => {
                        const amenList = s.amenities || [];
                        return (
                          <tr key={s.id} className="border-t border-[#0A1128]/10 hover:bg-[#F4F1EB]/30" data-testid={`simulation-row-${s.id}`}>
                            <td className="px-4 py-3 text-xs text-[#3A435E]">
                              {new Date(s.created_at).toLocaleDateString("pt-PT")}
                            </td>
                            <td className="px-4 py-3">
                              {s.email ? (
                                <a href={`mailto:${s.email}`} className="text-[#0A1128] hover:text-[#C5A059]">
                                  {s.email}
                                </a>
                              ) : (
                                <span className="text-[#3A435E]/50 italic text-xs">anónimo</span>
                              )}
                            </td>
                            <td className="px-4 py-3 uppercase text-xs font-medium">{s.type}</td>
                            <td className="px-4 py-3 text-xs">{ZONE_LABELS[s.location] || s.location}</td>
                            <td className="px-4 py-3">
                              {amenList.length === 0 ? (
                                <span className="text-[#3A435E]/50 italic text-xs">—</span>
                              ) : (
                                <div className="flex flex-wrap gap-1 max-w-[220px]">
                                  {amenList.map((a) => (
                                    <span key={a} className="inline-flex px-2 py-0.5 text-[10px] tracking-wide bg-[#0A1128]/6 text-[#0A1128] border border-[#0A1128]/10">
                                      {AMENITY_LABELS[a] || a}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              {s.al_status ? (
                                <span className={`inline-flex px-2.5 py-1 text-[10px] tracking-[0.14em] uppercase ${AL_STYLE[s.al_status] || "bg-[#0A1128]/8 text-[#3A435E]"}`}>
                                  {AL_LABELS[s.al_status] || s.al_status}
                                </span>
                              ) : "—"}
                            </td>
                            <td className="px-4 py-3 text-center text-xs">{s.nights}</td>
                            <td className="px-4 py-3 text-right text-xs text-[#3A435E]">
                              {s.monthly_own ? `€${Math.round(s.monthly_own)}` : "—"}
                            </td>
                            <td className="px-4 py-3 text-right font-medium">€{Math.round(s.monthly_net)}</td>
                            <td className="px-4 py-3 text-right text-[#C5A059]">€{Math.round(s.yearly_net)}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={s.email_sent ? "text-[#2D6A4F]" : "text-[#7B86A4]"}>
                                {s.email_sent ? "✓" : "—"}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })()}
          </TabsContent>

          {/* AI PRICING */}
          <TabsContent value="ai_pricing" className="mt-8">
            <AiPricingPanel houses={houses} onHousesRefresh={loadAll} />
          </TabsContent>

          {/* CALENDAR */}
          <TabsContent value="calendar" className="mt-8">
            <BookingCalendar houses={houses} bookings={bookings} onBookingChanged={loadAll} />
          </TabsContent>
        </Tabs>
      </main>
      <Footer />
    </div>
  );
};

export default AdminPage;
